import React, { createContext, useContext, useState, useEffect } from 'react';
import axiosInstance from './axiosInstance';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL;

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const noLogin = {
  email: `guest-${Date.now()}@preview.com`, // unique timestamp marker
  username: 'Guest',
  role: 'user',
  token: null
};
const [email, setEmail] = useState('');
const [username, setUsername] = useState('');
const [user, setUser] = useState(() => {
  const localStorageUser = localStorage.getItem('user');
  const sessionStorageUser = sessionStorage.getItem('user');
  
    return localStorageUser
      ? JSON.parse(localStorageUser)
      : sessionStorageUser
        ? JSON.parse(sessionStorageUser)
        : noLogin; // never null
  });
  

  const login = (email, token, userData, remember = true) => {
    const userObj = { email, token, ...userData };
    setUser(userObj);
    
    if (remember) {
      localStorage.setItem('user', JSON.stringify(userObj));
    } else {
      sessionStorage.setItem('user', JSON.stringify(userObj));
    }
  };
  const [googleDriveID, setGoogleDriveID] = useState(''); 
  const [googleDriveLink, setGoogleDriveLink] = useState(null); 
  const [isStudent, setIsStudent] = useState(() => {
    return sessionStorage.getItem('isStudent') === 'true';
  });
  const [quizFinished, setQuizFinished] = useState('');
  const [viewState, setViewState] = useState('scoreConfirmation');
  const navigate = useNavigate();
  
  

  // Update localStorage whenever user changes
  useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    }
  }, [user]);

  

   const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    sessionStorage.removeItem('user');
  };

  // Immediate sync between state and storage
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'user') {
        setUser(e.newValue ? JSON.parse(e.newValue) : null);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

 

  useEffect(() => {
    const syncUserState = () => {
      const storedUser = JSON.parse(localStorage.getItem('user') || sessionStorage.getItem('user') || 'null');
      if (storedUser && (!user || storedUser.token !== user?.token)) {
        console.log('[USER CONTEXT] Syncing user from storage');
        setUser(storedUser);
      }
    };
    // Check immediately on load
    syncUserState();

  // Set up storage event listener
    const handleStorageChange = (e) => {
      if (e.key === 'user') {
        syncUserState();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [user]);

  const getToken = () => {
    if (user?.token) {
      console.log('✓ Token found in UserContext');
      return user.token;
    }

    // 2. Check localStorage.user (with safe parsing)
    try {
      const localStorageUser = localStorage.getItem('user');
      if (localStorageUser) {
        const parsedUser = JSON.parse(localStorageUser);
        if (parsedUser?.token) {
          console.log('✓ Token found in localStorage.user');
          return parsedUser.token;
        }
      }
    } catch (e) {
      console.warn('Failed to parse localStorage.user', e);
    }

    // 3. Check sessionStorage.user (with safe parsing)
    try {
      const sessionStorageUser = sessionStorage.getItem('user');
      if (sessionStorageUser) {
        const parsedUser = JSON.parse(sessionStorageUser);
        if (parsedUser?.token) {
          console.log('✓ Token found in sessionStorage.user');
          return parsedUser.token;
        }
      }
    } catch (e) {
      console.warn('Failed to parse sessionStorage.user', e);
    }

    // 4. Check root-level localStorage.token
    const localStorageToken = localStorage.getItem('token');
    if (localStorageToken) {
      console.log('✓ Token found in localStorage.token');
      return localStorageToken;
    }

    // 5. Check root-level sessionStorage.token
    const sessionStorageToken = sessionStorage.getItem('token');
    if (sessionStorageToken) {
      console.log('✓ Token found in sessionStorage.token');
      return sessionStorageToken;
    }

    // 6. Check sessionStorage.userEmail (specific case)
    if (sessionStorage.getItem('userEmail')) {
      const sessionToken = sessionStorage.getItem('token');
      if (sessionToken) {
        console.log('✓ Token found in sessionStorage.token (with userEmail)');
        return sessionToken;
      }
    }

    console.error('✗ Token not found in any storage location');
    return null;
  };

  const getEmail = () => {
    // Always return a string, never undefined
    return user?.email
      || JSON.parse(localStorage.getItem('user'))?.email
      || JSON.parse(sessionStorage.getItem('user'))?.email
      || ''; // fallback safe string
  };
  const isGuest = () => getEmail().startsWith('guest-');

  // Automatically update axios token when user.token changes
  useEffect(() => {
    const token = getToken();
    if (token) {
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axiosInstance.defaults.headers.common['Authorization'];
    }
  }, [user?.token]);

  
  return (
    <UserContext.Provider value={{ 
      user,
      getToken,
      getEmail,
      isGuest,
      setUser,
      login,
      logout,
      googleDriveLink, 
      setGoogleDriveLink, 
      googleDriveID, 
      setGoogleDriveID, 
      isStudent, 
      setIsStudent,
      quizFinished, 
      setQuizFinished,
      viewState, 
      setViewState,
      username, 
      setUsername,
      email, 
      setEmail 
    }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => useContext(UserContext);